/**
 * Per-bin lap-speed statistics shared by the chart divergence band and the
 * map zone gradient. Both consumers MUST bin through this module — sharing
 * one series is what makes the map's reddest bin and the band's peak bin
 * the same bin by construction.
 *
 * Pass 29: binning consumes SpeedTrace series (the recalibrated frame the
 * zone chart plots) instead of raw odometer buckets — see speedTraces.ts for
 * why the frame choice is a correctness issue, not a plumbing detail.
 */
import { SAMPLE_INTERVAL, MIN_BAND_BINS } from '../../../config/chartDisplay'
import { median } from './robustStats'
import { MIN_LAPS_FOR_CONSISTENCY } from '../../../config/consistencyScoring'
import { type ConsistencyDisplayBounds } from './consistencyThresholds'
import { type SpeedTrace } from './speedTraces'
import { maxOf } from '../../../../utils/extremes'

/** Exclusion-filtered traces plus the bin frame and spread series they yield.
 *  The chart band and the map gradient both start from this ONE context, so
 *  their lap set, bin count, and spread values agree by construction. */
export interface ZoneSpreadContext {
  traces: SpeedTrace[]
  startDist: number
  numBins: number
  spreads: (number | null)[]
}

/** null = the zone can't support the statistic (too few laps or bins). */
export function computeZoneSpreadContext(
  traces: SpeedTrace[],
  excludedLapIds: Set<string>,
  displayBounds: ConsistencyDisplayBounds
): ZoneSpreadContext | null {
  const included = traces.filter((t) => !excludedLapIds.has(t.id))
  if (included.length < MIN_LAPS_FOR_CONSISTENCY) return null

  const startDist = displayBounds.startDistance
  const numBins = Math.ceil(
    (displayBounds.endDistance - startDist) / SAMPLE_INTERVAL
  )
  if (numBins < MIN_BAND_BINS) return null

  return {
    traces: included,
    startDist,
    numBins,
    spreads: computeBinSpeedSpreads(included, startDist, numBins),
  }
}

/** Index of the first element whose `distance` is >= `target`, via binary
 *  search over an ascending-by-distance array (`points.length` when every
 *  element is < target). Local to this file — see `perLapBinSpeeds` for why
 *  the ascending-order precondition holds. */
function lowerBoundByDistance(
  points: readonly { distance: number }[],
  target: number
): number {
  let lo = 0
  let hi = points.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (points[mid].distance < target) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** Average speed (km/h) per bin for one lap; undefined where the lap has no data. */
export function perLapBinSpeeds(
  trace: SpeedTrace,
  startDist: number,
  numBins: number
): (number | undefined)[] {
  const sums = new Array<number>(numBins).fill(0)
  const counts = new Array<number>(numBins).fill(0)
  const end = startDist + numBins * SAMPLE_INTERVAL

  // trace.points is sorted ascending by distance — every producer builds it
  // 1:1 from GPS points in acquisition order (tracesFromRecalibratedLaps /
  // tracesFromRawLaps / tracesFromChartTelemetry in speedTraces.ts all map
  // over an already-ordered point series with no reordering). That lets us
  // binary-search the first in-range point instead of scanning the whole
  // lap for every zone.
  const points = trace.points
  let i = lowerBoundByDistance(points, startDist)
  for (; i < points.length; i++) {
    const p = points[i]
    if (p.distance > end) break
    // Floor-division bin index shared by every consumer of this module —
    // the divergence band and the map zone gradient both bin through here,
    // which is what keeps the chart band and the map's reddest region
    // aligned.
    const idx = Math.floor((p.distance - startDist) / SAMPLE_INTERVAL)
    if (idx < 0 || idx >= numBins) continue
    sums[idx] += p.speed
    counts[idx] += 1
  }
  return sums.map((s, i) => (counts[i] > 0 ? s / counts[i] : undefined))
}

/**
 * Worst absolute deviation from the bin median of per-lap mean speed, for
 * every bin — the literal "how far is the farthest lap off here" number, so
 * a single divergent lap registers instead of being trimmed away (2026-07-20
 * screenshot regression: a lap 7 km/h off read as 100% consistent under the
 * old leave-one-out spread). null = insufficient data in that bin (fewer
 * than MIN_LAPS_FOR_CONSISTENCY values > 1 km/h). `traces` must already be
 * exclusion-filtered.
 */
export function computeBinSpeedSpreads(
  traces: SpeedTrace[],
  startDist: number,
  numBins: number
): (number | null)[] {
  return binUsableLapValues(traces, startDist, numBins).map((values) => {
    if (values.length < MIN_LAPS_FOR_CONSISTENCY) return null
    const med = median(values.map((v) => v.value))
    return maxOf(values.map((v) => Math.abs(v.value - med)))
  })
}

/** Median lap speed (km/h) per bin; null where fewer than
 *  MIN_LAPS_FOR_CONSISTENCY laps have usable data in that bin. */
export function binMedianSpeeds(
  traces: SpeedTrace[],
  startDist: number,
  numBins: number
): (number | null)[] {
  return binUsableLapValues(traces, startDist, numBins).map((values) =>
    values.length >= MIN_LAPS_FOR_CONSISTENCY
      ? median(values.map((v) => v.value))
      : null
  )
}

/** One lap's usable mean speed in one bin. lapIdx indexes the `traces` array
 *  passed in — stable across bins, so consumers can attribute deviations to
 *  a specific lap. */
export interface BinLapValue {
  lapIdx: number
  value: number
}

/** Usable per-lap mean speeds for every bin (speeds ≤ 1 km/h dropped as
 *  no-data noise), tagged with the owning lap's index. Single source for the
 *  spread/median stats above AND the curve row's per-lap attribution, so bin
 *  membership can never drift apart between them. */
export function binUsableLapValues(
  traces: SpeedTrace[],
  startDist: number,
  numBins: number
): BinLapValue[][] {
  const perLap = traces.map((t) => perLapBinSpeeds(t, startDist, numBins))
  const bins: BinLapValue[][] = []
  for (let bin = 0; bin < numBins; bin++) {
    const values: BinLapValue[] = []
    for (let lapIdx = 0; lapIdx < perLap.length; lapIdx++) {
      const s = perLap[lapIdx][bin]
      if (s !== undefined && s > 1) values.push({ lapIdx, value: s })
    }
    bins.push(values)
  }
  return bins
}
